import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { SchedulePolicyKind } from 'services/pangolin/protos/schedules_pb';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { SearchInput } from 'ui/standard/input/search_input/search_input';
import { CUSTOM_IP_SENTINEL, PolicyEntry, getPolicyEntryID, getPolicyEntryLabel } from 'ui/schedules/policy_entry/policy_entry_logic';
import baseStyles from './policy_input.module.css';
import { PolicyInputStore } from './policy_input_store';
import {
  RemovablePolicyEntry,
  SearchPolicyEntry,
  SearchPolicyEntryProps,
} from 'ui/schedules/policy_entry/policy_entry';
import { CustomInputModal, InputModalType } from './custom_input_modal/custom_input_modal';
import PolicyInputMessages from './policy_input_messages';

type PolicyInputStyles = {
  inputButton: string;
  plusIcon: string;
  placeholder: string;
};

const themedStyles: ThemedStyles<PolicyInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    plusIcon: baseStyles.daylightPlusIcon,
    placeholder: baseStyles.daylightPlaceholder,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    plusIcon: baseStyles.midnightPlusIcon,
    placeholder: baseStyles.midnightPlaceholder,
  },
};


type PolicyEntryDisplayProps = {
  store: PolicyInputStore;
  entry: PolicyEntry;
};

const PolicyEntryDisplay = observer(({ store, entry }: PolicyEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(entry);
  }), [store, entry]);

  return <RemovablePolicyEntry entry={entry} onRemoveClick={onRemoveClick}/>;
});

type PolicyInputDisplayProps = {
  store: PolicyInputStore;
  placeholder: string;
};

const PolicyInputDisplay = observer(({ store, placeholder }: PolicyInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.modal.showModal();
  }), [store]);
  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div>
          <div className={styles.plusIcon}/>
        </div>
      </button>
      {store.entries.map((entry) =>
        <PolicyEntryDisplay store={store} entry={entry} key={getPolicyEntryID(entry)}/>)}
    </>
  );
});

type PolicyInputProps = {
  placeholder: string;
  store: PolicyInputStore;
};

@observer export class PolicyInput extends React.Component<PolicyInputProps> {
  @computed get currentEntryIDs(): Map<string, true> {
    const map: Map<string, true> = new Map();
    const ids = this.props.store.entries.map(getPolicyEntryID);
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (entry: PolicyEntry, text: string): boolean => {
    if (entry.kind === SchedulePolicyKind.P_CUSTOM) {
      return true;
    }
    if (this.currentEntryIDs.has(getPolicyEntryID(entry))) {
      return false;
    }
    const trimmed = text.trim().toLocaleLowerCase();
    if (trimmed) {
      const label = getPolicyEntryLabel(entry).toLocaleLowerCase();
      return label.indexOf(trimmed) >= 0;
    }
    return true;
  };

  DisplayComponent = () => {
    return <PolicyInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: entry, ...restProps }: Omit<SearchPolicyEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      if (entry.kind === SchedulePolicyKind.P_CUSTOM) {
        if (entry.custom === CUSTOM_IP_SENTINEL) {
          this.props.store.customIP.showModal();
        } else {
          this.props.store.customDomain.showModal();
        }
      } else {
        this.props.store.commitEntry(entry);
        this.props.store.search.modal.hideModal();
      }
    }), [this.props.store, entry]);
    return <SearchPolicyEntry data={entry} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return (
      <>
        <SearchInput
          placeholder={this.props.placeholder}
          store={this.props.store.search}
          data={this.props.store.options}
          filterFunc={this.filterEntry}
          keyFunc={getPolicyEntryID}
          ItemComponent={this.ItemComponent}
          DisplayComponent={this.DisplayComponent}
        />
        <CustomInputModal
          store={this.props.store.customDomain}
          onCommit={this.props.store.commitDomain}
          instructions={PolicyInputMessages.customDomain()}
          saveMessage={PolicyInputMessages.saveCustom()}
          invalidMessage={PolicyInputMessages.invalidDomain()}
          type={InputModalType.URL_NONSTRICT}
        />
        <CustomInputModal
          store={this.props.store.customIP}
          onCommit={this.props.store.commitIP}
          instructions={PolicyInputMessages.customIP()}
          saveMessage={PolicyInputMessages.saveCustom()}
          invalidMessage={PolicyInputMessages.invalidIP()}
        />
      </>
    );
  }
}
